import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "../../../components/sidebar";
import { useNavigate, useOutletContext } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../style.scss";
import SearchIcon from "../../../icons/SearchIcon";
import LocationIcon from "../../../icons/LocationIcon";
import EditGreyIcon from "../../../icons/EditGreyIcon";
import DeleteGreyIcon from "../../../icons/DeleteGreyIcon";
import ThreeDotsIcon from "../../../icons/ThreeDotsIcon";
import SourceIcon from "../../../icons/SourceIcon";
import DestinationIcon from "../../../icons/DestinationIcon";
import BuildingIcon from "../../../icons/BuildingIcon";
import CalendarGreyIcon from "../../../icons/CalendarGreyIcon";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import PlusIcon from "../../../icons/PlusIcon";
import { getMileageList, updateMileage } from "../../../core/data/redux/mileage/_mileageAction";
import { mainCategoryList } from "../../../services/category.service";
import { getPropertyListForFilter } from "../../../core/data/redux/property/_propertyAction";
import { DeleteModalType } from "../../../constants/utlis";
import DeleteModal from "../../../components/deleteModal";
import { formatDate, formatDateString } from "../../../constants/date";
import { CommonPagination } from "../../../core/components/common/CommnPagination";
import { apiPageLimit } from "../../../constants/common";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import EnableRecurringModal from "../../../core/components/modals/enableRecurringModal";

const Mileage: React.FC = () => {
  const { isSidebarOpen } = useOutletContext<{
    isSidebarOpen: boolean;
    onToggleSidebar: () => void;
  }>();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const routes = all_routes;

  const mileageDetails = useSelector((state: any) => state?.mileage);
  const mileageData = mileageDetails?.mileageList;
  const totalCount = mileageDetails?.totalCount;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const pageSize = apiPageLimit;

  const [mainCategories, setMainCategories] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>("");
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [selectedMileageType, setSelectedMileageType] = useState<"" | "all" | "recurring" | "non-recurring">("");

  const [modalType, setModalType] = useState<DeleteModalType>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserUuid, setSelectedUserUuid] = useState<string | null>(null);
  const [showRecurringEnableModal, setShowRecurringEnableModal] = useState(false);
  const [selectedMileageForEdit, setSelectedMileageForEdit] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredProperties = allProperties.filter((p) =>
    `${p.name} ${p.address}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch mileage list on component mount
  useEffect(() => {
    dispatch(getMileageList({}));
    getMainCategoryList();
    getAllPropertyList();
  }, [dispatch]);

  // get main category list
  const getMainCategoryList = async () => {
    try {
      const response = await mainCategoryList();
      const data = response?.data?.data || [];

      const mapped = data.map((cat: any) => ({
        label: cat.name,
        value: cat.uuid,
      }));

      setMainCategories([{ label: "Choose Activity", value: "" }, ...mapped]);
      return;
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  // getPropertyListForFilter
  const getAllPropertyList = async () => {
    try {
      const response = await dispatch(getPropertyListForFilter({}));
      const allProps = response?.payload?.data || [];
      setAllProperties(allProps);
      return;
    } catch (error) {
      console.error("Failed to fetch all property:", error);
    }
  };

  // Debounce search
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Fetch properties with debounce
  const fetchProperties = useCallback(
    debounce(
      (
        search: string,
        selectedMainCategory: string,
        page: number,
        selectedProperty: string,
        selectedMileageType: string,
      ) => {
        const params: any = { page, limit: pageSize };
        if (search) params.search = search;
        if (selectedMainCategory) params.main_category = selectedMainCategory;
        if (selectedProperty) params.property = selectedProperty;
        if (selectedMileageType) {
          if (selectedMileageType === "all") {
            params.recurring_filter = "";
          }
          else if (selectedMileageType === "recurring") {
            params.recurring_filter = true;
          }
          else if (selectedMileageType === "non-recurring") {
            params.recurring_filter = false;
          }
        }
        dispatch(getMileageList(params));
      },
      500
    ),
    [dispatch]
  );

  // Run when searchTerm or selected changes
  useEffect(() => {
    fetchProperties(searchTerm, selectedMainCategory, page, selectedProperty, selectedMileageType);
  }, [
    searchTerm,
    selectedMainCategory,
    page,
    selectedProperty,
    selectedMileageType,
    fetchProperties,
  ]);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpenDeleteModal = (type: DeleteModalType, uuid: string) => {
    setModalType(type);
    setSelectedUserUuid(uuid);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUserUuid(null);
  };

  const handleClearAllFilters = () => {
    setSelectedMainCategory("");
    setSelectedProperty("");
    setSearchTerm("");
    setSelectedPropertyName("");
    setSelectedMileageType("");
  }

  const handlerecurringToggle = async (mileageId: string, wantsToEnable: boolean) => {
    console.log('wantsToEnable >>>> ', wantsToEnable, mileageId);
    if (!mileageId) return;

    // CASE 1: User wants to ENABLE recurring (false → true)
    if (wantsToEnable) {
      setSelectedMileageForEdit(mileageId);
      setShowRecurringEnableModal(true);
      return;
    }

    // CASE 2: User wants to DISABLE recurring (true → false) → Allow it
    try {
      await dispatch(updateMileage({ uuid: mileageId, values: { is_recurring: false } }));
    }
    catch (error) {
      console.error("Failed to update recurring data:", error);
    }
    finally {
      await refetchMileageListWithCurrentFilters();
    }
  }

  const refetchMileageListWithCurrentFilters = async () => {
    const params: any = {
      page,
      limit: pageSize,
    };

    if (searchTerm) params.search = searchTerm;
    if (selectedMainCategory) params.main_category = selectedMainCategory;
    if (selectedProperty) params.property = selectedProperty;

    if (selectedMileageType) {
      if (selectedMileageType === "all") {
        params.recurring_filter = "";
      } else if (selectedMileageType === "recurring") {
        params.recurring_filter = true;
      } else if (selectedMileageType === "non-recurring") {
        params.recurring_filter = false;
      }
    }

    await dispatch(getMileageList(params));
  };

  return (
    <div className="layout">
      <Sidebar isOpen={isSidebarOpen} />

      <main className="content">
        <div className="profileSettings mileage-tracking-page-section">
          <div className="rounded mileage-tracking-page-inner">
            <div className="add-properties-heading d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
              <h2 className="mb-0">Mileage Tracking</h2>

              <button
                type="button"
                className="btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center  btn-block"
                onClick={() => navigate(routes.addMileage)}
              >
                <PlusIcon />
                Log Mileage
              </button>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-sm-12 ">
                <div className="row-boder-line p-3">
                  <div className="row">
                    {/* Search log hours */}
                    <div className="col-12 my-1 col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-5">
                      <div className="input-group commonInput bg-light bg-white">
                        <span className="input-group-text bg-white border-end-0 ">
                          <SearchIcon />
                        </span>
                        <input
                          type="text"
                          placeholder="Search trip by purpose or route"
                          className="name form-control border-start-0"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Category select */}
                    <div className="col-12 my-1 col-sm-12 col-md-12 col-lg-3 col-xl-3 col-xxl-2">
                      <select
                        className="form-select form-control commonInput"
                        value={selectedMainCategory}
                        onChange={(e) =>
                          setSelectedMainCategory(e.target.value)
                        }
                      >
                        <option value="" disabled hidden>
                          Choose Activity
                        </option>

                        {mainCategories.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Property dropdown */}
                    <div className="col-12 my-1 col-sm-12 col-md-12 col-lg-3 col-xl-3 col-xxl-2"
                      ref={dropdownRef}
                    >
                      <div className="dropdown log-hour-list-page-dropdown">
                        <button
                          className="form-select d-flex justify-content-between align-items-center form-control commonInput"
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                          {selectedPropertyName || "Select Property "}
                          <i
                            className={`bi bi-chevron-${isDropdownOpen ? "up" : "down"}`}
                          />
                        </button>

                        {isDropdownOpen && (
                          <div
                            className="dropdown-menu proprty-dropdown show w-100 p-3 shadow-sm mt-1 bg-light"
                            style={{ maxHeight: "initial" }}
                          >
                            <div className="input-group mb-3 commonInput bg-light bg-white">
                              <span className="input-group-text bg-white border-end-0 ">
                                <SearchIcon />
                              </span>
                              <input
                                type="text"
                                placeholder="Search properties by name or address"
                                className="name form-control border-start-0"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                              />
                            </div>

                            {filteredProperties.length > 0 ? (
                              filteredProperties.map((property, index) => (
                                <div
                                  key={property?.uuid || index}
                                  className="property-drop form-check d-flex align-items-left mb-3 justify-content-between form-control commonInput bg-light"
                                  onClick={() => {
                                    setSelectedProperty(property?.uuid);
                                    setSelectedPropertyName(property?.name);
                                    setIsDropdownOpen(false);
                                  }}
                                  style={{ cursor: "pointer" }}
                                >
                                  <label className="form-check-label ms-2">
                                    <div className="label-inner-content d-flex flex-column gap-2">
                                      <strong>{property.name}</strong>
                                      <small className="text-muted d-flex align-items-center">
                                        <LocationIcon />
                                        {property.address}, {property?.city},{" "}
                                        {property?.state} {property?.zipcode}
                                      </small>
                                    </div>
                                  </label>
                                  <input
                                    type="radio"
                                    className="form-check-input mt-1"
                                    name="propertyRadio"
                                    checked={
                                      selectedPropertyName === property?.name
                                    }
                                    readOnly
                                  />
                                </div>
                              ))
                            ) : (
                              <div className="text-muted text-center">
                                No results found.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Mileage Type select */}
                    <div className="col-12 my-1 col-sm-12 col-md-12 col-lg-3 col-xl-3 col-xxl-2">
                      <select
                        className="form-select form-control commonInput"
                        value={selectedMileageType}
                        onChange={(e) => setSelectedMileageType(e.target.value as any)}
                      >
                        <option value="" disabled hidden> Mileage Type</option>
                        <option value="all">All</option>
                        <option value="recurring">Recurring</option>
                        <option value="non-recurring">Non-Recurring</option>
                      </select>
                    </div>

                    <div className="col-12 my-1 col-sm-12 col-md-12 col-lg-3 col-xl-3 col-xxl-1">
                      <div className="clear-all-button">
                        <button onClick={() => handleClearAllFilters()}>Clear All</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row align-items-stretch">
              {mileageData?.length > 0 ? (
                mileageData?.map((mileage: any) => {
                  return (
                    <div className="mb-4 col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-3 ">
                      <div className="custom-card mx-auto h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <span
                              className={
                                mileage?.main_category?.name.toLowerCase() ==
                                  "material participation"
                                  ? "category-badge"
                                  : "category-badge yellow"
                              }
                            >
                              {mileage?.main_category?.name}
                            </span>

                            <div className="dropdown">
                              <button
                                className="three-dot-btn rotate90"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                              >
                                <ThreeDotsIcon />
                              </button>
                              <ul className="dropdown-menu dropdown-menu-end">
                                {/* <li
                                  onClick={(event) => {
                                    event.stopPropagation(); // Prevent click from bubbling to parent
                                    navigate(`/mileage/add/${mileage?.uuid}`);
                                  }}
                                >
                                  <a className="dropdown-item" href="#">
                                    <EditGreyIcon />
                                    Edit
                                  </a>
                                </li> */}
                                <li
                                  onClick={(event) => {
                                    event.stopPropagation(); // Prevent click from bubbling to parent
                                    handleOpenDeleteModal(
                                      "deleteMileage",
                                      mileage?.uuid
                                    );
                                  }}
                                >
                                  <a className="dropdown-item " href="#">
                                    <DeleteGreyIcon />
                                    Delete
                                  </a>
                                </li>
                              </ul>
                            </div>
                          </div>

                          <div className="mt-4 text-area d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <h5 className=" fw-bold mb-0 d-flex  flex-column">
                              <p className="home">
                                <SourceIcon />
                                {mileage?.source_name}
                              </p>
                              <p className="ocean">
                                <DestinationIcon />
                                {mileage?.destination_name}
                              </p>
                            </h5>

                            <div className="d-flex justify-content-end  time-text">
                              <span className="time-badge">
                                {mileage?.distance} mi
                              </span>
                            </div>
                          </div>
                          <p className="mb-1 mt-1 mileage-name">
                            {mileage?.sub_activity?.name}
                          </p>
                          <div className="mt-3 d-flex flex-wrap gap-1 justify-content-between">
                            <span className="tag tag-red me-0">
                              <BuildingIcon /> {mileage?.property?.name}
                            </span>
                            <span className="tag tag-grey me-0">
                              <CalendarGreyIcon />{" "}
                              {formatDateString(mileage?.date)}
                            
                            </span>
                            {mileage?.is_parent && (
                              <span className="swtich_btn">
                                <Form.Check // prettier-ignore
                                  label="Recurring"
                                  type="switch"
                                  id="custom-switch"
                                  checked={!!mileage?.is_recurring}
                                  onChange={(e) => {
                                    const tryingToTurnOn = e.target.checked;

                                    if (tryingToTurnOn) {
                                      e.preventDefault();
                                      setSelectedMileageForEdit(mileage?.uuid);
                                      setShowRecurringEnableModal(true);
                                    } else {
                                      handlerecurringToggle(mileage?.uuid, false);
                                    }
                                  }}
                                />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center w-100 py-4">No data found.</p>
              )}
            </div>

            {/* Pagination */}
            <CommonPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </main>

      <DeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        type={modalType}
        uuid={selectedUserUuid}
      />

      <EnableRecurringModal
        show={showRecurringEnableModal}
        onHide={() => {
          setShowRecurringEnableModal(false);
          setSelectedMileageForEdit(null);
        }}
        uuid={selectedMileageForEdit}
        userRole={"owner"}
        onSuccess={() => {
          setShowRecurringEnableModal(false);
          setSelectedMileageForEdit(null);
          refetchMileageListWithCurrentFilters();
        }}
      />

    </div>
  );
};

export default Mileage;
